import { Database } from '@/integrations/supabase/types';
import { PostgrestError } from '@supabase/supabase-js';
import {
  logAnalyticsEvent,
  logConversationCreated,
  logConversationCompleted,
  logAnalyticsError,
  AnalyticsEventType
} from '@/utils/analyticsLogger';
import { getSupabaseClient } from '@/utils/widgetContext';

// Types from Supabase schema
type Conversation = Database['public']['Tables']['conversations']['Row'];
type UserResponse = Database['public']['Tables']['user_responses']['Row'];
type Question = Database['public']['Tables']['questions']['Row'];
type ConversationContext = { question: string; answer: string | string[] | null };

const log = (context: string, message: string, data?: any) => {
    console.log(`[ChatDbService:${context}] ${message}`, data ? JSON.stringify(data).substring(0, 150) + '...' : '');
};

/**
 * Initializes a new conversation record in the database.
 * @param chatbotId - The ID of the chatbot.
 * @param sessionId - The session ID for the chat.
 * @returns The newly created conversation object or null if an error occurred.
 */
export const initializeConversation = async (
    chatbotId: string,
    sessionId: string
): Promise<Conversation | null> => {
    log('initializeConversation', 'Attempting to initialize conversation', { chatbotId, sessionId });

    // Validate inputs
    if (!chatbotId) {
        const errorMsg = 'Missing chatbotId parameter';
        logAnalyticsError(errorMsg, chatbotId, null, sessionId);
        console.error(`[ChatDbService:initializeConversation] ${errorMsg}`);
        return null;
    }

    if (!sessionId) {
        const errorMsg = 'Missing sessionId parameter';
        logAnalyticsError(errorMsg, chatbotId, null, sessionId);
        console.error(`[ChatDbService:initializeConversation] ${errorMsg}`);
        return null;
    }

    try {
        // Collect metadata for analytics
        const urlParams = new URLSearchParams(window.location.search);
        const metadata = {
            userAgent: navigator.userAgent,
            startingUrl: window.location.href,
            referrer: document.referrer || null,
            timestamp: new Date().toISOString(),
            // Extract UTM parameters if present
            utm_source: urlParams.get('utm_source') || null,
            utm_medium: urlParams.get('utm_medium') || null,
            utm_campaign: urlParams.get('utm_campaign') || null,
            // Store PII token if present for conversation details
            pii_token: urlParams.get('pii') || urlParams.get('pii_token') || null
        };

        const supabase = await getSupabaseClient();
        const { data, error } = await supabase
            .from('conversations')
            .insert({
                chatbot_id: chatbotId,
                session_id: sessionId,
                status: 'active',
                metadata,
                // context will be added later
            })
            .select()
            .single();

        if (error) {
            logAnalyticsError(
                `Failed to initialize conversation: ${error.message}`,
                chatbotId,
                null,
                sessionId,
                { error: { message: error.message, details: error.details, hint: error.hint } }
            );
            console.error('Error initializing conversation:', error.message, error.details, error.hint);
            return null;
        }

        // Log successful conversation creation
        logConversationCreated(chatbotId, data.id, sessionId, metadata);
        log('initializeConversation', 'Conversation initialized successfully', { conversationId: data.id });
        return data;
    } catch (e) {
        // Catch any unexpected errors
        const errorMsg = e instanceof Error ? e.message : 'Unknown error';
        logAnalyticsError(
            `Unexpected error initializing conversation: ${errorMsg}`,
            chatbotId,
            null,
            sessionId,
            { error: e }
        );
        console.error(`[ChatDbService:initializeConversation] Unexpected error:`, e);
        return null;
    }
};

/**
 * Saves a user's response to a specific question within a conversation.
 * @param conversationId - The ID of the conversation.
 * @param question - The question object being answered.
 * @param response - The user's response text or selected options.
 * @returns True if successful, false otherwise.
 */
export const saveUserResponse = async (
    conversationId: string | null,
    question: Question,
    response: string | string[],
): Promise<boolean> => {
    log('saveUserResponse', 'Attempting to save user response', { conversationId, questionId: question.id });
    console.log('Attempting to save user response with params:');
    console.log('  conversationId:', conversationId);
    console.log('  question.id:', question?.id);
    console.log('  response:', response);
    if (!conversationId || !question || !question.id) {
         log('saveUserResponse', 'Missing required data', { conversationId, questionId: question?.id });
         return false;
    }

    const responseData: Omit<UserResponse, 'id' | 'created_at'> = {
        conversation_id: conversationId,
        question_id: question.id,
        response_text: Array.isArray(response) ? null : response,
        response_options: Array.isArray(response) ? response : null,
    };

    console.log('Saving user response data:', responseData);

    const supabase = await getSupabaseClient();
    const { error } = await supabase.from('user_responses').insert(responseData);

    if (error) {
        log('saveUserResponse', 'Failed to save user response', { error });
        console.error('Error saving user response:', error.message, error.details, error.hint);
        return false;
    }

    log('saveUserResponse', 'User response saved successfully', { questionId: question.id });
    console.log('User response saved successfully for question:', question.id);
    return true;
};

/**
 * Saves a message to the database.
 * @param conversationId - The ID of the conversation.
 * @param messageText - The text content of the message.
 * @param isBot - Whether the message is from the bot (true) or user (false).
 * @param questionId - Optional question ID if the message is related to a specific question.
 * @returns True if successful, false otherwise.
 */
export const saveMessage = async (
    conversationId: string,
    messageText: string,
    isBot: boolean,
    questionId?: string
): Promise<boolean> => {
    log('saveMessage', 'Attempting to save message', { conversationId, isBot, questionId });

    if (!conversationId || !messageText.trim()) {
        log('saveMessage', 'Missing required data', { conversationId, messageText: messageText.substring(0, 50) });
        return false;
    }

    try {
        const messageData = {
            conversation_id: conversationId,
            content: messageText,
            type: isBot ? 'bot' : 'user', // Use type field to indicate bot vs user
            metadata: {
                is_bot: isBot,
                question_id: questionId || null
            }
        };

        const supabase = await getSupabaseClient();
        const { error } = await supabase.from('messages').insert(messageData);

        if (error) {
            log('saveMessage', 'Failed to save message', { error });
            console.error('Error saving message:', error.message, error.details, error.hint);
            return false;
        }

        log('saveMessage', 'Message saved successfully', { conversationId, isBot });
        return true;
    } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Unknown error';
        log('saveMessage', 'Unexpected error saving message', { error: errorMsg });
        console.error('[ChatDbService:saveMessage] Unexpected error:', e);
        return false;
    }
};

/**
 * Updates an existing message in the database.
 * @param conversationId - The ID of the conversation.
 * @param messageText - The updated text content of the message.
 * @param isBot - Whether the message is from the bot (true) or user (false).
 * @param questionId - Optional question ID if the message is related to a specific question.
 * @returns True if successful, false otherwise.
 */
export const updateMessageInDb = async (
    conversationId: string,
    messageText: string,
    isBot: boolean,
    questionId?: string
): Promise<boolean> => {
    log('updateMessageInDb', 'Attempting to update message in database', { conversationId, isBot, questionId });

    if (!conversationId || !messageText.trim()) {
        log('updateMessageInDb', 'Missing required data', { conversationId, messageText: messageText.substring(0, 50) });
        return false;
    }

    try {
        // Find the most recent message for this conversation and type
        const supabase = await getSupabaseClient();
        const { data: existingMessages, error: fetchError } = await supabase
            .from('messages')
            .select('id')
            .eq('conversation_id', conversationId)
            .eq('type', isBot ? 'bot' : 'user')
            .order('created_at', { ascending: false })
            .limit(1);

        if (fetchError) {
            log('updateMessageInDb', 'Failed to fetch existing message', { error: fetchError });
            console.error('Error fetching existing message:', fetchError.message, fetchError.details, fetchError.hint);
            return false;
        }

        if (!existingMessages || existingMessages.length === 0) {
            // No existing message found, create a new one
            return await saveMessage(conversationId, messageText, isBot, questionId);
        }

        // Update the existing message
        const messageId = existingMessages[0].id;
        const { error } = await supabase
            .from('messages')
            .update({
                content: messageText,
                metadata: {
                    is_bot: isBot,
                    question_id: questionId || null,
                    updated_at: new Date().toISOString()
                }
            })
            .eq('id', messageId);

        if (error) {
            log('updateMessageInDb', 'Failed to update message', { error });
            console.error('Error updating message:', error.message, error.details, error.hint);
            return false;
        }

        log('updateMessageInDb', 'Message updated successfully', { conversationId, messageId, isBot });
        return true;
    } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Unknown error';
        log('updateMessageInDb', 'Unexpected error updating message', { error: errorMsg });
        console.error('[ChatDbService:updateMessageInDb] Unexpected error:', e);
        return false;
    }
};

/**
 * Updates the context array and updated_at timestamp for a conversation.
 * @param conversationId - The ID of the conversation to update.
 * @param conversationContext - The full, updated context array.
 * @returns True if successful, false otherwise.
 */
export const updateConversationContext = async (
    conversationId: string,
    conversationContext: ConversationContext[]
): Promise<boolean> => {
    log('updateConversationContext', 'Attempting to update context', { conversationId });
    console.log('Attempting to update conversation context with params:');
    console.log('  conversationId:', conversationId);
    console.log('  conversationContext:', JSON.stringify(conversationContext)); // Stringify for better object view
     if (!conversationId) {
         log('updateConversationContext', 'Missing conversationId');
         return false;
    }

    const supabase = await getSupabaseClient();
    const { error } = await supabase
        .from('conversations')
        .update({
            context: conversationContext,
            updated_at: new Date().toISOString(),
        })
        .eq('id', conversationId);

    console.log('Updating conversation with data:', { context: conversationContext, updated_at: new Date().toISOString() });
    console.log('Targeting conversation ID:', conversationId);

    if (error) {
        log('updateConversationContext', 'Failed to update conversation context', { error });
        console.error('Error updating conversation context:', error.message, error.details, error.hint);
        return false;
    }

    log('updateConversationContext', 'Conversation context updated successfully');
    console.log('Conversation context updated successfully for conversation:', conversationId);
    return true;
};

/**
 * Updates the context array and updated_at timestamp for a conversation.
 * @param conversationId - The ID of the conversation to update.
 * @param piiToken - The full, updated context array.
 * @returns True if successful, false otherwise.
 */
export const updateConversationPiiToken = async (
    conversationId: string,
    piiToken: string
): Promise<boolean> => {
    log('updateConversationPiiToken', 'Attempting to update piiToken', { piiToken });
    console.log('Attempting to update conversation context with params:');
    console.log('  conversationId:', conversationId);
    console.log('  piiToken:', piiToken);
    if (!conversationId) {
        log('updateConversationPiiToken', 'Missing conversationId');
        return false;
    }

    const supabase = await getSupabaseClient();

    const { data: currentConversation, error: fetchError } = await supabase
        .from('conversations')
        .select('chatbot_id, session_id, metadata')
        .eq('id', conversationId)
        .single();

    if (fetchError) {
        console.error('Error fetching conversation:', fetchError.message, fetchError.details, fetchError.hint);
        return false;
    }

    // Prepare updated metadata with completion information
    const existingMetadata = currentConversation?.metadata || {};
    const updatedMetadata = {
        ...existingMetadata,
        pii_token: piiToken
    };

    const { error } = await supabase
        .from('conversations')
        .update({
            metadata: updatedMetadata
        })
        .eq('id', conversationId);

    if (error) {
        console.error('Error updating conversation piiToken:', error.message, error.details, error.hint);
        return false;
    }

    log('updateConversationPiiToken', 'Conversation piiToken updated successfully');
    console.log('Conversation piiToken updated successfully for conversation:', conversationId);
    return true;
};

/**
 * Marks a conversation as completed in the database.
 * @param conversationId - The ID of the conversation.
 * @param finalContext - The final context array to save.
 * @returns True if successful, false otherwise.
 */
export const markConversationCompleted = async (
    conversationId: string,
    finalContext: ConversationContext[]
): Promise<boolean> => {
    log('markConversationCompleted', 'Attempting to mark conversation complete', { conversationId });

    if (!conversationId) {
        const errorMsg = 'Missing conversationId parameter';
        logAnalyticsError(errorMsg, null, conversationId, null);
        console.error(`[ChatDbService:markConversationCompleted] ${errorMsg}`);
        return false;
    }

    try {
        // Get the current conversation to retrieve chatbotId and sessionId
        const supabase = await getSupabaseClient();
        const { data: currentConversation, error: fetchError } = await supabase
            .from('conversations')
            .select('chatbot_id, session_id, metadata')
            .eq('id', conversationId)
            .single();

        if (fetchError) {
            logAnalyticsError(
                `Failed to fetch conversation before marking as completed: ${fetchError.message}`,
                null,
                conversationId,
                null,
                { error: { message: fetchError.message, details: fetchError.details, hint: fetchError.hint } }
            );
            console.error('Error fetching conversation:', fetchError.message, fetchError.details, fetchError.hint);
            return false;
        }

        // Prepare updated metadata with completion information
        const existingMetadata = currentConversation?.metadata || {};
        const updatedMetadata = {
            ...existingMetadata,
            completed: true,
            completed_at: new Date().toISOString(),
            finalUrl: window.location.href,
            userAgent: navigator.userAgent
        };

        const { error } = await supabase
            .from('conversations')
            .update({
                status: 'completed',
                ended_at: new Date().toISOString(),
                context: finalContext, // Ensure final context is saved
                updated_at: new Date().toISOString(),
                metadata: updatedMetadata
            })
            .eq('id', conversationId);

        if (error) {
            logAnalyticsError(
                `Failed to mark conversation as completed: ${error.message}`,
                currentConversation?.chatbot_id,
                conversationId,
                currentConversation?.session_id,
                { error: { message: error.message, details: error.details, hint: error.hint } }
            );
            console.error('Error marking conversation completed:', error.message, error.details, error.hint);
            return false;
        }

        // Log successful completion
        logConversationCompleted(
            currentConversation?.chatbot_id,
            conversationId,
            currentConversation?.session_id,
            { finalContext, completedAt: new Date().toISOString() }
        );

        log('markConversationCompleted', 'Conversation marked as completed successfully');
        return true;
    } catch (e) {
        // Catch any unexpected errors
        const errorMsg = e instanceof Error ? e.message : 'Unknown error';
        logAnalyticsError(
            `Unexpected error marking conversation as completed: ${errorMsg}`,
            null,
            conversationId,
            null,
            { error: e }
        );
        console.error(`[ChatDbService:markConversationCompleted] Unexpected error:`, e);
        return false;
    }
};

// Add other DB functions if needed, e.g., fetching conversation history